/**
 * Registration Page
 */

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../context/AuthContext';
import SEO from '../../components/common/SEO';
import { Eye, EyeOff, AlertCircle, Ship, Users, Anchor } from 'lucide-react';

function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, socialLogin, error, clearError } = useAuth();

  const typeParam = searchParams.get('type');
  const initialUserType = typeParam === 'owner' ? 'yacht_owner' : (typeParam === 'helper' ? 'helper' : '');
  const [step, setStep] = useState(initialUserType ? 2 : 1);
  const [formData, setFormData] = useState({
    user_type: initialUserType,
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    use_whatsapp: false,
    yacht_name: '',
    yacht_type: 'motor',
    yacht_length: '',
    length_unit: 'meters',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Social login state
  const [socialData, setSocialData] = useState(null); // { provider, token, email, first_name, last_name }

  const handleChange = (e) => {
    clearError();
    setValidationError('');
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUserTypeSelect = (type) => {
    setFormData({ ...formData, user_type: type });
    setStep(socialData ? 'social-phone' : 2);
  };

  // Google login handler
  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      setValidationError('');
      try {
        const data = await socialLogin('google', tokenResponse.access_token, {});

        if (data.needs_completion) {
          setSocialData({
            provider: 'google',
            token: tokenResponse.access_token,
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
          });
          setFormData(prev => ({
            ...prev,
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name,
          }));
          if (formData.user_type) {
            setStep('social-phone');
          } else {
            setStep(1);
          }
        } else {
          const dashboardPath = data.user.user_type === 'helper' ? '/helper' : '/owner';
          navigate(dashboardPath);
        }
      } catch (err) {
        setValidationError(err.message || 'Google sign-in failed. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    onError: (errorResponse) => {
      console.error('Google OAuth error:', errorResponse);
      setValidationError(errorResponse?.error_description || 'Google sign-in failed. Please try again.');
    },
    onNonOAuthError: (error) => {
      console.error('Google non-OAuth error:', error);
      if (error?.type === 'popup_closed') {
        setValidationError('Google sign-in was cancelled.');
      } else {
        setValidationError('Google sign-in could not load. Please check your browser settings and try again.');
      }
    },
  });

  // Submit social registration (user_type + phone collected)
  const handleSocialComplete = async (e) => {
    e.preventDefault();
    if (!formData.phone.trim()) {
      setValidationError('Phone number is required');
      return;
    }

    setLoading(true);
    try {
      const data = await socialLogin(socialData.provider, socialData.token, {
        user_type: formData.user_type,
        phone: formData.phone,
        use_whatsapp: formData.use_whatsapp,
        first_name: socialData.first_name,
        last_name: socialData.last_name,
        // Yacht details if yacht owner and we have them
        ...(formData.user_type === 'yacht_owner' && formData.yacht_name ? {
          yacht_name: formData.yacht_name,
          yacht_type: formData.yacht_type,
          yacht_length: formData.length_unit === 'feet'
            ? parseFloat(formData.yacht_length) * 0.3048
            : parseFloat(formData.yacht_length),
        } : {}),
      });

      if (data.user) {
        const dashboardPath = data.user.user_type === 'helper' ? '/helper' : '/owner';
        navigate(dashboardPath);
      }
    } catch (err) {
      // Error handled by AuthContext
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSubmit = (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setValidationError('Password must be at least 8 characters');
      return;
    }

    if (!formData.phone.trim()) {
      setValidationError('Phone number is required');
      return;
    }

    if (formData.user_type === 'yacht_owner') {
      setStep(3);
      return;
    }

    handleFinalSubmit();
  };

  const handleFinalSubmit = async () => {
    setLoading(true);

    try {
      const { confirmPassword, length_unit, ...registerData } = formData;

      if (formData.user_type === 'yacht_owner' && formData.yacht_length) {
        registerData.yacht_length = length_unit === 'feet'
          ? parseFloat(formData.yacht_length) * 0.3048
          : parseFloat(formData.yacht_length);
      }

      const data = await register(registerData);
      const dashboardPath = data.user.user_type === 'helper' ? '/helper' : '/owner';
      navigate(dashboardPath);
    } catch (err) {
      // Error is handled by AuthContext
    } finally {
      setLoading(false);
    }
  };

  const handleYachtSubmit = (e) => {
    e.preventDefault();

    if (!formData.yacht_name.trim()) {
      setValidationError('Please enter your yacht name');
      return;
    }

    if (!formData.yacht_length || parseFloat(formData.yacht_length) <= 0) {
      setValidationError('Please enter a valid yacht length');
      return;
    }

    handleFinalSubmit();
  };

  const displayError = error || validationError;

  // Google button component
  const GoogleButton = ({ label }) => (
    <button
      type="button"
      onClick={() => googleLogin()}
      disabled={loading}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-navy-200 rounded-apple-lg hover:bg-navy-50 transition-all duration-300 disabled:opacity-50"
    >
      <svg width="20" height="20" viewBox="0 0 24 24">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      <span className="text-sm font-medium text-navy-700">{label || 'Continue with Google'}</span>
    </button>
  );

  // Social phone completion step (after social sign-in, new user needs phone + maybe yacht)
  if (step === 'social-phone') {
    // If yacht owner, also ask for yacht details in same step
    const isYachtOwner = formData.user_type === 'yacht_owner';

    return (
      <div>
        <button
          onClick={() => { setStep(1); }}
          className="text-navy-600 hover:text-navy-800 mb-6 text-sm"
        >
          &larr; Back
        </button>

        <div className="text-center mb-6">
          <div className="h-14 w-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="h-7 w-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-navy-900">
            Welcome, {socialData.first_name}!
          </h1>
          <p className="text-navy-500 font-light mt-1">
            Just a few more details to get started
          </p>
        </div>

        {displayError && (
          <div className="mb-6 p-4 bg-red-50 rounded-apple flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
            <p className="text-red-700 text-sm">{displayError}</p>
          </div>
        )}

        <form onSubmit={isYachtOwner ? (e) => {
          e.preventDefault();
          if (!formData.phone.trim()) { setValidationError('Phone number is required'); return; }
          if (!formData.yacht_name.trim()) { setValidationError('Please enter your yacht name'); return; }
          if (!formData.yacht_length || parseFloat(formData.yacht_length) <= 0) { setValidationError('Please enter a valid yacht length'); return; }
          handleSocialComplete(e);
        } : handleSocialComplete} className="space-y-5">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-navy-700 mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="input-field"
              placeholder="+1 234 567 8900"
              required
            />
            <p className="mt-1 text-xs text-navy-500">Required for communication</p>

            {formData.user_type === 'helper' && (
              <label className="flex items-center mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="use_whatsapp"
                  checked={formData.use_whatsapp}
                  onChange={(e) => setFormData({ ...formData, use_whatsapp: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-navy-700">I use WhatsApp on this number</span>
              </label>
            )}
          </div>

          {isYachtOwner && (
            <>
              <div className="pt-2 border-t border-navy-200">
                <h3 className="text-lg font-semibold text-navy-900 mb-4 mt-4">Yacht Details</h3>
              </div>
              <div>
                <label htmlFor="yacht_name" className="block text-sm font-medium text-navy-700 mb-2">
                  Yacht Name
                </label>
                <input
                  type="text"
                  id="yacht_name"
                  name="yacht_name"
                  value={formData.yacht_name}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="e.g., Sea Breeze"
                  required
                />
              </div>
              <div>
                <label htmlFor="yacht_type" className="block text-sm font-medium text-navy-700 mb-2">
                  Yacht Type
                </label>
                <select
                  id="yacht_type"
                  name="yacht_type"
                  value={formData.yacht_type}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="motor">Motor Yacht</option>
                  <option value="sailing">Sailing Yacht</option>
                  <option value="catamaran">Catamaran</option>
                  <option value="superyacht">Superyacht</option>
                </select>
              </div>
              <div>
                <label htmlFor="yacht_length" className="block text-sm font-medium text-navy-700 mb-2">
                  Length (LOA)
                </label>
                <div className="flex gap-3">
                  <input
                    type="number"
                    id="yacht_length"
                    name="yacht_length"
                    value={formData.yacht_length}
                    onChange={handleChange}
                    className="input-field flex-1"
                    placeholder="e.g., 15"
                    min="1"
                    step="0.1"
                    required
                  />
                  <select
                    name="length_unit"
                    value={formData.length_unit}
                    onChange={handleChange}
                    className="input-field w-28"
                  >
                    <option value="meters">Meters</option>
                    <option value="feet">Feet</option>
                  </select>
                </div>
              </div>
            </>
          )}

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <span className="spinner h-5 w-5 mr-2" />
                  Creating account...
                </span>
              ) : (
                'Complete Registration'
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Step 1: User Type Selection
  if (step === 1) {
    return (
      <div>
        <SEO
          title="Create Your Free Account"
          description="Join AnchorPals for free. Register as a yacht owner to find mooring helpers, or as a helper to earn money assisting with docking at 500+ ports worldwide."
          path="/register"
        />
        <div className="flex items-center justify-center mb-8">
          <img src="/anchorpals-logo.jpg" alt="AnchorPals" className="h-32 w-32 sm:h-36 sm:w-36 rounded-3xl" />
          <span className="ml-6 text-5xl sm:text-6xl font-bold text-navy-900 tracking-tight">
            AnchorPals
          </span>
        </div>

        {socialData && (
          <div className="mb-6 p-4 bg-green-50 rounded-apple text-center">
            <p className="text-green-800 text-sm font-medium">
              Signed in as {socialData.email}
            </p>
            <p className="text-green-600 text-xs mt-1">Choose your account type below</p>
          </div>
        )}

        <h1 className="text-3xl font-bold text-navy-900 tracking-tight mb-2">
          Join AnchorPals
        </h1>
        <p className="text-navy-500 font-light mb-6">
          How would you like to use AnchorPals?
        </p>

        <div className="space-y-3">
          <button
            onClick={() => handleUserTypeSelect('yacht_owner')}
            className="w-full p-5 bg-white border border-navy-200 hover:border-primary-600 rounded-apple-lg text-left transition-all duration-300 group"
          >
            <div className="flex items-start">
              <div className="h-11 w-11 bg-navy-100 group-hover:bg-primary-50 rounded-apple flex items-center justify-center transition-colors duration-300">
                <Ship className="h-5 w-5 text-navy-600 group-hover:text-primary-600 transition-colors duration-300" />
              </div>
              <div className="ml-4">
                <h3 className="text-base font-medium text-navy-900">Owner/Captain</h3>
                <p className="text-navy-500 font-light mt-0.5 text-sm">
                  I need help with mooring my yacht when arriving at ports
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleUserTypeSelect('helper')}
            className="w-full p-5 bg-white border border-navy-200 hover:border-primary-600 rounded-apple-lg text-left transition-all duration-300 group"
          >
            <div className="flex items-start">
              <div className="h-11 w-11 bg-navy-100 group-hover:bg-primary-50 rounded-apple flex items-center justify-center transition-colors duration-300">
                <Users className="h-5 w-5 text-navy-600 group-hover:text-primary-600 transition-colors duration-300" />
              </div>
              <div className="ml-4">
                <h3 className="text-base font-medium text-navy-900">Helper</h3>
                <p className="text-navy-500 font-light mt-0.5 text-sm">
                  I want to assist owners/captains and earn tips
                </p>
              </div>
            </div>
          </button>
        </div>

        <p className="mt-8 text-center text-navy-600">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    );
  }

  // Step 3: Yacht Details (for yacht owners)
  if (step === 3) {
    return (
      <YachtDetailsStep
        formData={formData}
        handleChange={handleChange}
        handleYachtSubmit={handleYachtSubmit}
        setStep={setStep}
        loading={loading}
        displayError={displayError}
      />
    );
  }

  // Step 2: Registration Form (email/password flow)
  return (
    <div>
      <button
        onClick={() => setStep(1)}
        className="text-navy-600 hover:text-navy-800 mb-6 text-sm"
      >
        &larr; Back to selection
      </button>

      <div className="flex items-center justify-center mb-6">
        <img src="/anchorpals-logo.jpg" alt="AnchorPals" className="h-32 w-32 sm:h-36 sm:w-36 rounded-3xl" />
        <span className="ml-6 text-5xl sm:text-6xl font-bold text-navy-900 tracking-tight">
          AnchorPals
        </span>
      </div>

      <h1 className="text-3xl font-bold tracking-tight text-navy-900 mb-2">
        Create your account
      </h1>
      <p className="text-navy-600 mb-6">
        {formData.user_type === 'helper'
          ? 'Join as a mooring helper'
          : 'Join as an owner/captain'}
      </p>

      {/* Google Sign-In */}
      <GoogleButton label="Sign up with Google" />

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-navy-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-navy-400 font-light">or register with email</span>
        </div>
      </div>

      {displayError && (
        <div className="mb-6 p-4 bg-red-50 rounded-apple flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
          <p className="text-red-700 text-sm">{displayError}</p>
        </div>
      )}

      <form onSubmit={handleAccountSubmit} className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="first_name" className="block text-sm font-medium text-navy-700 mb-2">
              First Name
            </label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className="input-field"
              placeholder="John"
              required
            />
          </div>
          <div>
            <label htmlFor="last_name" className="block text-sm font-medium text-navy-700 mb-2">
              Last Name
            </label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className="input-field"
              placeholder="Smith"
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-navy-700 mb-2">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="input-field"
            placeholder="you@example.com"
            required
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-navy-700 mb-2">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="input-field"
            placeholder="+1 234 567 8900"
            required
          />
          <p className="mt-1 text-xs text-navy-500">Required for communication with {formData.user_type === 'helper' ? 'owners/captains' : 'helpers'}</p>

          <label className="flex items-center mt-3 cursor-pointer">
            <input
              type="checkbox"
              name="use_whatsapp"
              checked={formData.use_whatsapp}
              onChange={(e) => setFormData({ ...formData, use_whatsapp: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-navy-700">I use WhatsApp on this number</span>
          </label>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-navy-700 mb-2">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="input-field pr-12"
              placeholder="********"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-600"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          <p className="mt-1 text-xs text-navy-500">Minimum 8 characters</p>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-navy-700 mb-2">
            Confirm Password
          </label>
          <input
            type={showPassword ? 'text' : 'password'}
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className="input-field"
            placeholder="********"
            required
          />
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <span className="spinner h-5 w-5 mr-2" />
                Creating account...
              </span>
            ) : (
              'Create Account'
            )}
          </button>
        </div>

        <p className="text-xs text-navy-500 text-center">
          By creating an account, you agree to our{' '}
          <Link to="/terms" className="text-primary-600 hover:underline">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-primary-600 hover:underline">
            Privacy Policy
          </Link>
        </p>
      </form>

      <p className="mt-8 text-center text-navy-600">
        Already have an account?{' '}
        <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
          Sign in
        </Link>
      </p>
    </div>
  );
}

// Step 3: Yacht Details (for yacht owners only)
function YachtDetailsStep({ formData, handleChange, handleYachtSubmit, setStep, loading, displayError }) {
  return (
    <div>
      <button
        onClick={() => setStep(2)}
        className="text-navy-600 hover:text-navy-800 mb-6 text-sm"
      >
        &larr; Back to account details
      </button>

      <div className="flex items-center mb-6">
        <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center">
          <Anchor className="h-6 w-6 text-primary-600" />
        </div>
        <div className="ml-4">
          <h1 className="text-2xl font-bold tracking-tight text-navy-900">
            Your Yacht Details
          </h1>
          <p className="text-navy-600">
            Tell us about your yacht
          </p>
        </div>
      </div>

      {displayError && (
        <div className="mb-6 p-4 bg-red-50 rounded-apple flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
          <p className="text-red-700 text-sm">{displayError}</p>
        </div>
      )}

      <form onSubmit={handleYachtSubmit} className="space-y-5">
        <div>
          <label htmlFor="yacht_name" className="block text-sm font-medium text-navy-700 mb-2">
            Yacht Name
          </label>
          <input
            type="text"
            id="yacht_name"
            name="yacht_name"
            value={formData.yacht_name}
            onChange={handleChange}
            className="input-field"
            placeholder="e.g., Sea Breeze"
            required
          />
        </div>

        <div>
          <label htmlFor="yacht_type" className="block text-sm font-medium text-navy-700 mb-2">
            Yacht Type
          </label>
          <select
            id="yacht_type"
            name="yacht_type"
            value={formData.yacht_type}
            onChange={handleChange}
            className="input-field"
            required
          >
            <option value="motor">Motor Yacht</option>
            <option value="sailing">Sailing Yacht</option>
            <option value="catamaran">Catamaran</option>
            <option value="superyacht">Superyacht</option>
          </select>
        </div>

        <div>
          <label htmlFor="yacht_length" className="block text-sm font-medium text-navy-700 mb-2">
            Length (LOA)
          </label>
          <div className="flex gap-3">
            <input
              type="number"
              id="yacht_length"
              name="yacht_length"
              value={formData.yacht_length}
              onChange={handleChange}
              className="input-field flex-1"
              placeholder="e.g., 15"
              min="1"
              step="0.1"
              required
            />
            <select
              name="length_unit"
              value={formData.length_unit}
              onChange={handleChange}
              className="input-field w-28"
            >
              <option value="meters">Meters</option>
              <option value="feet">Feet</option>
            </select>
          </div>
          <p className="mt-1 text-xs text-navy-500">
            Length Overall - the total length of your yacht
          </p>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <span className="spinner h-5 w-5 mr-2" />
                Creating account...
              </span>
            ) : (
              'Complete Registration'
            )}
          </button>
        </div>

        <p className="text-xs text-navy-500 text-center">
          You can update your yacht details later in your profile
        </p>
      </form>
    </div>
  );
}

export default RegisterPage;
